/* eslint-disable */
import block from 'propmods';
import React, { useState, useEffect, useCallback } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router';
import { getOperationsTranslated } from '../../api';
import { routes } from '../../lib/routes';
import { Operation } from '../../model/operation';
import { Skill, AliceSkill } from '../../model/skill';
import Footer from '../Footer/Footer';
import Link from '../Link/Link';
import Menu from '../Menu/Menu';
import PublishingPage from '../PublishingPage/PublishingPage';
import DeploymentStatus from '../statuses/DeploymentStatus';
import TestPage from '../TestPage/TestPage';
import { Page } from '../Typo/Typo';
import PromotionPage from '../PromotionPage/PromotionPage';
import RatingPage from '../ReviewPage/RatingPage';
import { getCanEditApp, isIframe, makeSafe } from '../../utils';
import ResourcesPage from '../ResourcesPage/ResourcesPage';
import BillingPage from '../Billing/BillingPage/BillingPage';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import SmartHomeTestPage from '../SmartHomeTestPage/SmartHomeTestPage';
import { isPrivateThereminvoxSkill } from '../../utils/thereminvox';
import { DonationPage } from '../DonationPage/DonationPage';
import { useInterval } from '../../hooks/useInterval';
import MonitoringPage from '../MonitoringPage/MonitoringPage';
import { SettingsPage } from '../SettingsPage/SettingsPage';
import { useSkillPageMenuItems } from '../../hooks/useSkillPageMenuItems';
import { draftStatusLabels } from '../../locale/ru/draftStatus';
import { CurrentAliceAppContext } from '../../context/aliceAppContext';
import { SharingPage } from '../SharingPage/SharingPage';
import { EditorFieldsPage } from '../EditorFieldsPage/EditorFieldsPage';
import { FeatureFlagsPage } from '../FeatureFlagsPage/FeatureFlagsPage';
import { getIsArabicSkillName } from 'client/utils/skill';
import AccountLinkingSettings from '../AccountLinkingSettings/AccountLinkingSettings';

import './SkillPage.scss';

const b = block('SkillPage');

interface Props {
    skill: Skill;
}

export const SkillPage: React.FC<Props> = ({ skill }) => {
    const location = useLocation();
    const history = useHistory();

    const [operations, setOperations] = useState<Operation[]>([]);

    const addOperation = useCallback(
        (operation: Operation) => {
            setOperations([operation, ...operations]);
        },
        [operations],
    );

    const { menuItems, display } = useSkillPageMenuItems(skill);

    const fetchOperations = useCallback(
        makeSafe(async () => {
            setOperations(await getOperationsTranslated(skill.id));
        }),
        [],
    );

    useEffect(() => void fetchOperations(), [skill.id]);

    useInterval(fetchOperations, 1 * 60 * 1000);

    const onPageTypeChange = (isDraft: boolean, page: string) => {
        const pathname = isDraft
            ? location.pathname.replace(`/${page}`, `/draft/${page}`)
            : location.pathname.replace(`/draft/${page}`, `/${page}`);

        history.push(pathname);
    };

    const onSettingsTypeChange = (isDraft: boolean) => onPageTypeChange(isDraft, 'settings');
    const onTestingTypeChange = (isDraft: boolean) => onPageTypeChange(isDraft, 'test');

    const channel = skill.draft.channel;
    const status = skill.draft.status;

    const disabled = !getCanEditApp(skill);

    return (
        <ErrorBoundary withHomepageLink>
            <CurrentAliceAppContext.Provider
                value={{
                    aliceApp: skill,
                    operations,
                    addOperation,
                    fetchOperations: () => {},
                    refreshAliceApp: () => {},
                }}
            >
                <Page>
                    {!isIframe() && (
                        <Page.Head>
                            <Page.HeadContent>
                                <h1 {...b('headline', { rtl: getIsArabicSkillName(skill) })}>
                                    <Link to={routes.app.skill(skill.id)}>
                                        {skill.onAir ? skill.name : skill.draft.name}
                                    </Link>
                                </h1>
                                <div {...b('status')}>
                                    <DeploymentStatus aliceApp={skill} />
                                    {!isPrivateThereminvoxSkill(skill.draft) && (
                                        <span {...b('status-draft')}>
                                            {(draftStatusLabels as any)[channel][status]}
                                        </span>
                                    )}
                                </div>
                                <div {...b('nav')}>
                                    <Menu items={menuItems} />
                                </div>
                            </Page.HeadContent>
                        </Page.Head>
                    )}
                    <Page.Content>
                        <Page.Page>
                            <Switch>
                                <Route
                                    exact
                                    path={routes.app.skill()}
                                    render={(props) => (
                                        <PublishingPage {...props} skill={skill} operations={operations} disabled={disabled} />
                                    )}
                                />
                                <Route
                                    path={routes.app.skillPage(true, 'settings')}
                                    render={() => <SettingsPage onTypeChange={onSettingsTypeChange} isDraft disabled={disabled} />}
                                />
                                <Route
                                    path={routes.app.skillPage(false, 'settings')}
                                    render={() => <SettingsPage onTypeChange={onSettingsTypeChange} isDraft={false} disabled={disabled}  />}
                                />
                                {display.test && (
                                    <Route
                                        path={routes.app.skillPage(true, 'test')}
                                        render={(props) => (
                                            <TestPage
                                                {...props}
                                                skill={skill as AliceSkill}
                                                onTypeChange={onTestingTypeChange}
                                                isDraft
                                            />
                                        )}
                                    />
                                )}
                                {display.test && (
                                    <Route
                                        path={routes.app.skillPage(false, 'test')}
                                        render={(props) => (
                                            <TestPage
                                                {...props}
                                                skill={skill as AliceSkill}
                                                onTypeChange={onTestingTypeChange}
                                                isDraft={false}
                                            />
                                        )}
                                    />
                                )}
                                {display.smartHomeTest && (
                                    <Route
                                        path={routes.app.skillPage(true, 'test')}
                                        render={() => (
                                            <SmartHomeTestPage
                                                skill={skill}
                                                onTypeChange={onTestingTypeChange}
                                                isDraft
                                            />
                                        )}
                                    />
                                )}
                                {display.smartHomeTest && (
                                    <Route
                                        path={routes.app.skillPage(false, 'test')}
                                        render={() => (
                                            <SmartHomeTestPage
                                                skill={skill}
                                                onTypeChange={onTestingTypeChange}
                                                isDraft={false}
                                            />
                                        )}
                                    />
                                )}
                                {display.promotion && (
                                    <Route
                                        path={routes.app.skillPage(false, 'promotion')}
                                        render={() => <PromotionPage skill={skill} />}
                                    />
                                )}
                                {display.rating && (
                                    <Route
                                        exact
                                        path={routes.app.skillRating()}
                                        render={(props) => <RatingPage {...props} skill={skill} />}
                                    />
                                )}

                                {display.resources && (
                                    <Route
                                        path={routes.app.skillResources('')}
                                        render={(props) => <ResourcesPage {...props} skill={skill} disabled={disabled} />}
                                    />
                                )}

                                {display.billing && (
                                    <Route
                                        path={routes.app.skillPage(false, 'billing')}
                                        render={(props) => <BillingPage {...props} skill={skill} disabled={disabled} />}
                                    />
                                )}

                                {display.monitoring && (
                                    <Route
                                        path={routes.app.skillPage(false, 'monitoring')}
                                        render={() => <MonitoringPage skill={skill} />}
                                    />
                                )}
                                {display.donation && (
                                    <Route
                                        path={routes.app.skillPage(false, 'donation')}
                                        render={() => <DonationPage disabled={disabled} />}
                                    />
                                )}
                                {display.sharing && (
                                    <Route
                                        path={routes.app.skillPage(false, 'sharing')}
                                        render={() => <SharingPage disabled={disabled} />}
                                    />
                                )}
                                {display.editorFields && (
                                    <Route
                                        path={routes.app.skillPage(false, 'editing')}
                                        render={() => <EditorFieldsPage disabled={disabled} />}
                                    />
                                )}
                                {display.featureFlags && (
                                    <Route
                                        path={routes.app.skillPage(false, 'feature-flags')}
                                        render={() => <FeatureFlagsPage disabled={disabled} />}
                                    />
                                )}
                                {display.authorization && <AccountLinkingSettings skill={skill} disabled={disabled} />}
                            </Switch>
                        </Page.Page>
                    </Page.Content>

                    <Footer feedback={routes.documentation.aliceSkills.feedback} channel={skill.channel} />
                </Page>
            </CurrentAliceAppContext.Provider>
        </ErrorBoundary>
    );
};
